/**
 * User: Rob Richard
 * Date: 9/17/14
 * Time: 3:56 PM
 */

'use strict';

const _ = require('underscore');
const get = require('lodash.get');
const tracking = require('dibs-tracking');
const { URL } = require('bunsen/helpers/uri.es');
const PurchaseTracking = require('../../global/mixins/purchaseTracking.es');
const helpers = require('./checkout-helpers.es');
const redirects = require('./checkout-redirects.es');
const storage = require('./checkout-storage.es');
const trackHelpers = require('./checkout-track-helpers.es');
const {constants: authConstants} = require('../../auth/authModalLoader').default;
const {fireAuthModalEvent, fireFormInteractionEvent} = require('../../auth/trackingHelpers.es');

const triggerCheckoutAction = (actionType, trigger, section, entry) => {
    const label = [];

    trigger = redirects.isUserComesFromEmail() ? 'email' : trigger;

    if (entry) {
        label.push(`entry=${entry}`);
    }
    if (trigger) {
        const triggers = [trigger, trackHelpers.getCallToAction()];
        if (redirects.isUserComesFromAmp()) {
            triggers.push('amppage');
        }
        label.push(`trigger=${triggers.join('-')}`);
    }
    if (section) {
        label.push(`section=${section}`);
    }
    return tracking.trackEvent({
        category: 'checkout',
        action: `checkout ${actionType}`,
        label: label.join('|')
    });
};
const triggerFormInteractionAction = label => {
    tracking.trackEvent({
        category: 'checkout',
        action: 'checkout form interaction',
        label
    });
};
const triggerClickToDealerStorefrontAction = label => {
    tracking.trackEvent({
        category: 'dealer click',
        action: 'click to dealer storefront',
        label
    });
};
const trackCheckoutSurvey = label => {
    const { step } = storage.getSurveyInfo() || {};
    const eventLabel = [label, step].join('|');
    triggerFormInteractionAction(eventLabel);
};
const triggerCheckoutAuthModalEvent = (action, flowStep) => {
    const label = trackHelpers.getGATransactionType();
    fireAuthModalEvent({ action, label }, flowStep);
};

const trackEvent = module.exports = {

    trackFormErrors(step, errors, category) {
        Object.keys(errors).forEach(field => {
            if (errors[field]) {
                tracking.trackFormError({
                    action: [category || 'checkout', step, field.toLowerCase()].join('|'),
                    label: errors[field].toLowerCase()
                });
            }
        });
    },
    triggerStaleLoginEvent(interaction) {
        const gaObj = trackHelpers.getGAVerificationObj(interaction);
        tracking.trackEvent(gaObj);
    },
    triggerFormInteractionEvent(interaction, additional) {
        fireFormInteractionEvent(authConstants.REGISTER, interaction, {}, additional);
    },
    triggerProceedToCheckoutEvent(action, flow) {
        action = _.isUndefined(action) || action === authConstants.STALE_LOGIN_FLOW ? authConstants.LOGIN : action;
        switch (action) {
            case authConstants.LOGIN:
                action = (flow === authConstants.STALE_LOGIN_FLOW ? 'verification' : '');
                break;
            case authConstants.FORGOT_PASSWORD:
                action = 'forgot password';
        }
        tracking.trackEvent({
            category: action.replace('-', ' '),
            action: `${action} form interaction`,
            label: 'click on register link - proceed to checkout'
        });
    },
    triggerEmbeddedRegistrationEvent(buyer, isNewUser) {
        if (isNewUser) {
            const steps = ['two', 'user-type'];
            const additional = trackHelpers.getAdditionalEmbeddedRegistrationGTM(buyer);
            steps.forEach((interaction) => trackEvent.triggerFormInteractionEvent(interaction, additional));
            trackEvent.triggerRegistrationEvent('complete');
        } else {
            trackEvent.triggerLoginCompleteEvent();
        }
    },
    triggerAction(label) {
        tracking.trackEvent({
            category: 'checkout form actions',
            action: helpers.isNegotiable() ? 'offer' : 'order',
            label
        });
    },
    getItemStatusEventLabel(item) {
        return [
            trackHelpers.getItemNegotiable(item),
            trackHelpers.getPrequotedShipping(item),
            trackHelpers.isReturnable(item)
        ].join('|');
    },
    triggerServiceError(label) {
        tracking.trackFormError({
            action: ['checkout', 'review', 'ecom'].join('|'),
            label
        });
    },
    triggerConfirmationAction(label) {
        return tracking.trackEvent({
            category: 'checkout confirmation actions',
            action: helpers.isNegotiable() ? 'offer' : 'order',
            label
        });
    },
    getEmailPurchaseAction() {
        const subAction = helpers.isPrivateOffer() ? 'accept offer' : 'purchase';
        const action = `email ${subAction} button`;
        const tmpl = get(URL, 'queryKey.e_tmpl');

        if (tmpl) {
            return `${action} - ${tmpl.replace(/\+/g, ' ')}`;
        } else {
            return `${action} - none`;
        }
    },
    triggerEmailPurchaseEntry(item = null) {
        tracking.trackEvent({
            category: 'purchase click',
            action: trackEvent.getEmailPurchaseAction(),
            label: trackEvent.getItemStatusEventLabel(item)
        });
    },
    trackSidebarElementExpanded(elementName) {
        switch (elementName) {
            case 'promoCode':
                trackEvent.triggerPromoCodeExpanded();
                break;
            case 'returnPolicy':
                trackEvent.triggerReturnPolicyExpanded();
        }
    },
    trackAmpPurchaseButtonClick(item) {
        const category = '';
        const action = 'pdp purchase button-amppage';
        const params = { label: trackHelpers.getPurchaseStatus(item) };
        PurchaseTracking.firePurchaseButtonTracking(item, category, action, params);
    },
    trackChatShow(step) {
        tracking.trackEvent({
            category: 'live chat',
            action: 'docked live chat expanded',
            label: step
        });
    },
    trackChatHide() {
        tracking.trackEvent({
            category: 'live chat',
            action: 'docked live chat closed'
        });
    },
    trackChatLoad() {
        tracking.trackEvent({
            category: 'live chat',
            action: 'docked live chat displayed'
        });
    },
    triggerRegistrationEvent: _.partial(triggerCheckoutAuthModalEvent, authConstants.REGISTER),
    triggerLoginEntryEvent: _.partial(triggerCheckoutAuthModalEvent, authConstants.LOGIN, 'entry'),
    triggerLoginCompleteEvent: _.partial(triggerCheckoutAuthModalEvent, authConstants.LOGIN, 'complete'),
    triggerCheckoutEntryAction: _.partial(triggerCheckoutAction, 'entry'),
    triggerCheckoutSaved: _.partial(triggerCheckoutAction, 'saved'),
    triggerCheckoutCompleted: _.partial(triggerCheckoutAction, 'complete'),
    triggerCheckoutInteraction: _.partial(triggerCheckoutAction, 'form initiated'),
    triggerCheckoutSurveyDisplayed: _.partial(trackCheckoutSurvey, 'abandon checkout survey displayed'),
    triggerCheckoutSurveySubmitted: _.partial(trackCheckoutSurvey, 'abandon checkout survey submitted'),
    triggerReturnPolicyExpanded: _.partial(triggerFormInteractionAction, 'dealer return policy expanded'),
    triggerPromoCodeExpanded: _.partial(triggerFormInteractionAction, 'promo code section expanded'),
    triggerEmailUsClicked: _.partial(triggerFormInteractionAction, 'email us clicked'),
    triggerMarketplaceSupportClicked: _.partial(triggerFormInteractionAction, 'marketplace support link clicked'),
    triggerShippingHover: _.partial(triggerFormInteractionAction, 'shipping - to be quoted tooltip displayed'),
    triggerCustomsHover: _.partial(triggerFormInteractionAction, 'additional customs tooltip displayed'),
    triggerCustomsDisplayed: _.once(_.partial(triggerFormInteractionAction, 'customs on review step displayed')),
    triggerCustomsChecked: _.partial(triggerFormInteractionAction, 'customs on review step selected'),
    triggerCustomsReviewHover: _.partial(triggerFormInteractionAction, 'additional customs tooltip displayed on review step'),
    triggerCustomsLearnMoreClicked: _.partial(triggerFormInteractionAction, 'additional customs learn more clicked'),
    triggerCustomsReviewLearnMoreClicked: _.partial(triggerFormInteractionAction, 'additional customs learn more clicked on review step'),
    triggerBuyerAssuranceTooltipAction: _.partial(triggerFormInteractionAction, 'buyer protection popup expanded'),
    triggerBuyerAssuranceViewDetailsAction: _.partial(triggerFormInteractionAction, 'buyer protection popup link clicked'),
    triggerConfirmationDealerLinkClicked: _.partial(triggerClickToDealerStorefrontAction, 'checkout confirmation dealer link'),
    triggerConfirmationDealerViewAllClicked: _.partial(triggerClickToDealerStorefrontAction, 'checkout confirmation dealer highlights view storefront')
};



// WEBPACK FOOTER //
// ./assets/js/checkout/helpers/checkout-track-event.es.js